import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "../../../lib/utils";
import {
  ShieldCheck,
  ShieldX,
  ToggleLeft,
  ToggleRight,
  Library,
  RefreshCw,
  Save,
  Copy,
  Trash2,
  Eye,
  Edit,
  CheckCircle2,
  XCircle,
  ChevronUp,
  ChevronDown,
  Search,
  Brain,
  Plus,
  Lock,
  FlaskConical,
  AlertTriangle,
  Loader2,
  Bot,
  Users,
  Shapes,
  Globe,
} from "lucide-react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import GatewayChatPanel from "../components/GatewayChatPanel";
import GatewayCedarEditor from "../components/GatewayCedarEditor";
import GatewayStatCard from "../components/GatewayStatCard";
import { GatewayPoliciesApi, GatewayAgentsApi } from "../Api/gatewayApi";
import { effectColor, copyToClipboard, formatTimestamp } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import type {
  Policy,
  PolicyStats,
  PolicyChatResponse,
  ChatMessage,
} from "../interfaces/gatewayInterfaces";
import "../../ManagementDialog.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import CardPagination from "../../CardPagination";
import "../AgenticGateway.css";

type Tab = "workspace" | "library";
type SortField = "createdAt" | "name";
type SortDir = "asc" | "desc";

interface LibraryFilters {
  search: string;
  effect: string;
  enabled: string;
  source: string;
}


function EffectBadge({ effect }: { effect: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border",
        effectColor(effect)
      )}
    >
      {effect}
    </span>
  );
}

function EnabledToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-1 text-xs transition-colors"
      title={enabled ? "Disable policy" : "Enable policy"}
    >
      {enabled ? (
        <ToggleRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <ToggleLeft className="w-5 h-5 text-muted-foreground/70" />
      )}
      <span className={enabled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}>
        {enabled ? "Enabled" : "Disabled"}
      </span>
    </button>
  );
}


// ════════════════════════════════════════════════════════════════════════
//  POLICY CHECK — mirrors the OG dashboard "Check policy" flow:
//  one Check button → /check → review popup → Save / Save anyway / (fix first)
// ════════════════════════════════════════════════════════════════════════

type CheckSeverity = "error" | "warn" | "ok";

/** Severity of a /check result → drives the save button (matches OG _checkSeverity). */
function policyCheckSeverity(c: any): CheckSeverity {
  if (!c) return "error";
  // broken syntax / duplicate @id / unregistered reference → must fix, never saveable
  if (c.valid === false || !!c.error || !!c.idDuplicate) return "error";
  if (c.allReferencesKnown === false) return "error";
  const effect = String(c.effect || "PERMIT").toUpperCase();
  const decision = String(c.decision || "").toUpperCase();
  const matched = (c.matchedPolicies || []).length;
  const baseline = String(c.baselineDecision || "").toUpperCase();
  const takesEffect =
    (effect === "PERMIT" && decision === "ALLOW") ||
    (effect === "FORBID" && decision === "DENY" && matched > 0);
  if (!takesEffect) return "warn"; // overridden / never matches / doesn't block
  if ((effect === "FORBID" && baseline === "ALLOW") || (effect === "PERMIT" && baseline === "ALLOW"))
    return "warn"; // overrides an existing permit / redundant
  return "ok";
}

function CheckMsgBox({
  tone,
  icon,
  head,
  detail,
}: {
  tone: CheckSeverity;
  icon: React.ReactNode;
  head: string;
  detail?: React.ReactNode;
}) {
  const cls =
    tone === "ok"
      ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
      : tone === "warn"
        ? "border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10"
        : "border-red-500/40 text-red-600 dark:text-red-400 bg-red-500/10";
  return (
    <div>
      <div className={cn("flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm", cls)}>
        <span className="shrink-0">{icon}</span>
        <strong className="font-semibold">{head}</strong>
      </div>
      {detail != null && detail !== "" && (
        <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{detail}</div>
      )}
    </div>
  );
}

/** Full /check result: syntax, real-data references, effect, conflicts, duplicate @id. */
function PolicyCheckResultView({ c }: { c: any }) {
  if (!c) return null;
  if (c.valid === false) {
    return (
      <CheckMsgBox tone="error" icon={<XCircle className="w-4 h-4" />} head="Invalid syntax" detail={c.error || ""} />
    );
  }
  const refs: any[] = c.references || [];
  const effect = String(c.effect || "PERMIT").toUpperCase();
  const decision = String(c.decision || "").toUpperCase();
  const matched: string[] = c.matchedPolicies || [];
  const baseline = String(c.baselineDecision || "").toUpperCase();
  const baseMatched = (c.baselineMatched || []).join(", ");

  return (
    <div className="space-y-2.5">
      <CheckMsgBox tone="ok" icon={<CheckCircle2 className="w-4 h-4" />} head="Valid Cedar syntax" />

      {c.warning && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10 text-[13px]">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{c.warning}</span>
        </div>
      )}

      {refs.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
            References — checked against real data
          </p>
          <div className="space-y-1">
            {refs.map((r, i) => (
              <div
                key={i}
                className={cn("flex items-center gap-2 text-[13px]", r.known ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}
              >
                {r.known ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0" />}
                <span>
                  {r.kind} <code className="font-mono ag-surface px-1 py-0.5 rounded text-foreground/90">{r.value}</code>{" "}
                  — {r.known ? "exists" : `not a registered ${r.kind} — typo?`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Effect — will this policy actually do something once saved? */}
      {c.allReferencesKnown === false ? (
        <CheckMsgBox
          tone="error"
          icon={<XCircle className="w-4 h-4" />}
          head="Won't take effect"
          detail="It names something that isn't registered (above), so no real request will ever match it."
        />
      ) : effect === "FORBID" ? (
        decision === "DENY" && matched.length > 0 ? (
          <CheckMsgBox
            tone="ok"
            icon={<CheckCircle2 className="w-4 h-4" />}
            head="Takes effect"
            detail={<>This policy <strong className="text-foreground/90">blocks</strong> that access.</>}
          />
        ) : (
          <CheckMsgBox
            tone="warn"
            icon={<AlertTriangle className="w-4 h-4" />}
            head="Doesn't block"
            detail="This forbid doesn't apply to its target."
          />
        )
      ) : decision === "ALLOW" ? (
        <CheckMsgBox
          tone="ok"
          icon={<CheckCircle2 className="w-4 h-4" />}
          head="Takes effect"
          detail={<>This policy <strong className="text-foreground/90">grants</strong> that access.</>}
        />
      ) : matched.length > 0 ? (
        <CheckMsgBox
          tone="warn"
          icon={<AlertTriangle className="w-4 h-4" />}
          head="Overridden"
          detail={
            <>
              A forbid/guardrail decides it instead:{" "}
              <code className="font-mono text-foreground/90">
                {matched.map((m) => (m === "__draft__" ? "this policy" : m)).join(", ")}
              </code>
              . Your permit doesn't take effect here.
            </>
          }
        />
      ) : (
        <CheckMsgBox
          tone="error"
          icon={<XCircle className="w-4 h-4" />}
          head="Never matches"
          detail="Nothing grants it → default-deny."
        />
      )}

      {/* Conflict — compare with vs without this draft against the same target. */}
      {c.allReferencesKnown && effect === "FORBID" && decision === "DENY" && baseline === "ALLOW" && (
        <CheckMsgBox
          tone="warn"
          icon={<AlertTriangle className="w-4 h-4" />}
          head="Overrides an existing permit"
          detail={
            <>
              Without this forbid, that access is granted by{" "}
              <code className="font-mono text-foreground/90">{baseMatched || "another policy"}</code>. This forbid
              overrides it — that permit becomes dead.
            </>
          }
        />
      )}
      {c.allReferencesKnown && effect === "PERMIT" && decision === "ALLOW" && baseline === "ALLOW" && (
        <CheckMsgBox
          tone="warn"
          icon={<AlertTriangle className="w-4 h-4" />}
          head="Redundant"
          detail={
            <>
              That access is already allowed by{" "}
              <code className="font-mono text-foreground/90">{baseMatched || "another policy"}</code> — this policy
              adds nothing.
            </>
          }
        />
      )}

      {/* Duplicate @id. */}
      {c.idDuplicate && (
        <CheckMsgBox
          tone="warn"
          icon={<AlertTriangle className="w-4 h-4" />}
          head="Duplicate @id"
          detail={
            <>
              This id is already used by policy{" "}
              <code className="font-mono text-foreground/90">{c.idDuplicateOf || ""}</code>. Give this policy a unique
              @id.
            </>
          }
        />
      )}
    </div>
  );
}

/**
 * The "Review policy" popup: single pre-save gate. Shows the /check result, then
 * offers Save (clean) / Save anyway (warnings) / nothing-but-fix-it (errors).
 */
function PolicyReviewDialog({
  open,
  onOpenChange,
  loading,
  result,
  error,
  saving,
  onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  loading: boolean;
  result: any | null;
  error: string | null;
  saving: boolean;
  onSave: () => void;
}) {
  const sev = result ? policyCheckSeverity(result) : "error";
  const canSave = !loading && !error && sev !== "error";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-xl">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <FlaskConical />
            </span>
            <div>
              <DialogTitle>Review policy</DialogTitle>
              <DialogDescription>Checked against your tenant's real registry and live policies.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          {loading ? (
            <div className="ag-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking…
            </div>
          ) : error ? (
            <div className="ag-alert" data-tone="danger">
              <AlertTriangle />
              <span>{error}</span>
            </div>
          ) : (
            <PolicyCheckResultView c={result} />
          )}
        </div>

        {!loading && (
          <DialogFooter className="management-dialog-footer">
            {!error && sev === "error" && (
              <span className="text-xs text-red-600 dark:text-red-400 mr-auto self-center">Fix the issue above, then re-check.</span>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Back to edit
            </Button>
            {!error && sev === "error" && (
              <Button disabled className="endpoint-primary-action opacity-45 cursor-not-allowed">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save
              </Button>
            )}
            {canSave && (
              <Button
                onClick={onSave}
                disabled={saving}
                className={cn(
                  sev === "warn" ? "ag-warning-button" : "endpoint-primary-action"
                )}
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                )}
                {sev === "warn" ? "Save anyway" : "Save"}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function GatewayPoliciesPage() {
  const { toast } = useToast();

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [statsRaw, setStatsRaw] = useState<PolicyStats>({ total: 0, active: 0, permit: 0, forbid: 0 });
  const [loading, setLoading] = useState(true);

  const stats = {
    total: policies.length,
    active: policies.filter((p) => p.enabled).length,
    permit: policies.filter((p) => p.effect === "PERMIT").length,
    forbid: policies.filter((p) => p.effect === "FORBID").length,
    llmAvailable: statsRaw.llmAvailable,
  };

  // A CISO priority action can hand a drafted brief to the assistant via ?seed= — it opens the workspace pre-filled.
  const [params] = useSearchParams();
  const seed = params.get("seed") || "";

  const [tab, setTab] = useState<Tab>("workspace");

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [lastChatResp, setLastChatResp] = useState<PolicyChatResponse | null>(null);
  const [policyCode, setPolicyCode] = useState("");
  const [saving, setSaving] = useState(false);
  // id of the policy currently being edited in the workspace (null = creating new)
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);

  const [filters, setFilters] = useState<LibraryFilters>({
    search: "",
    effect: "",
    enabled: "",
    source: "",
  });
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  // Server-side scope: the policies that govern a chosen agent (empty = all). Other filters refine client-side.
  const [agentFilter, setAgentFilter] = useState("");
  const [agentOptions, setAgentOptions] = useState<string[]>([]);

  const [viewPolicy, setViewPolicy] = useState<Policy | null>(null);
  const [deletePolicy, setDeletePolicy] = useState<Policy | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // manual Create / Edit-metadata modal (editId null = create)
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [cf, setCf] = useState({ policyName: "", description: "", policyText: "", effect: "PERMIT", priority: "100", tags: "" });
  // check-before-save state for the Create/Edit modal (mirrors OG proceed('create'|'edit'))
  const [cfChecking, setCfChecking] = useState(false);
  const [cfCheckResult, setCfCheckResult] = useState<any>(null);
  const [cfCheckError, setCfCheckError] = useState<string | null>(null);
  // editing any field invalidates the last check → must re-check before saving
  const setCfField = (k: string, v: string) => {
    setCf((p) => ({ ...p, [k]: v }));
    setCfCheckResult(null);
    setCfCheckError(null);
  };
  const resetCfCheck = () => {
    setCfCheckResult(null);
    setCfCheckError(null);
    setCfChecking(false);
  };
  const openCreateModal = () => {
    setEditId(null);
    setCf({ policyName: "", description: "", policyText: "", effect: "PERMIT", priority: "100", tags: "" });
    resetCfCheck();
    setCreateOpen(true);
  };
  const openEditModal = (p: Policy) => {
    setEditId(p.id);
    const rawTags = (p as any).tags;
    setCf({
      policyName: (p as any).policyName || p.name || "",
      description: p.description || "",
      policyText: p.policyText || "",
      effect: p.effect || "PERMIT",
      priority: String((p as any).priority ?? "100"),
      tags: Array.isArray(rawTags) ? rawTags.join(", ") : (rawTags || ""),
    });
    resetCfCheck();
    setCreateOpen(true);
  };
  const runCreateCheck = async () => {
    if (!cf.policyName.trim() || !cf.policyText.trim()) {
      toast({ title: "Policy name and Cedar text are required", variant: "destructive" });
      return;
    }
    setCfChecking(true);
    setCfCheckResult(null);
    setCfCheckError(null);
    try {
      setCfCheckResult(await GatewayPoliciesApi.check(cf.policyText));
    } catch (e: any) {
      setCfCheckError(e?.error || e?.message || "Check failed");
    } finally {
      setCfChecking(false);
    }
  };
  const handleCreateManual = async () => {
    if (!cf.policyName.trim() || !cf.policyText.trim()) {
      toast({ title: "Policy name and Cedar text are required", variant: "destructive" });
      return;
    }
    setCreateSaving(true);
    try {
      const body = {
        policyName: cf.policyName.trim(),
        description: cf.description.trim() || undefined,
        policyText: cf.policyText,
        effect: cf.effect,
        priority: parseInt(cf.priority, 10) || 100,
        tags: cf.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editId) {
        await GatewayPoliciesApi.update(editId, body);
        toast({ title: "Policy updated" });
      } else {
        await GatewayPoliciesApi.create({ ...body, source: "MANUAL", enabled: true });
        toast({ title: "Policy created" });
      }
      setCreateOpen(false);
      setEditId(null);
      setCf({ policyName: "", description: "", policyText: "", effect: "PERMIT", priority: "100", tags: "" });
      resetCfCheck();
      await loadData();
    } catch (err: any) {
      toast({ title: editId ? "Update failed" : "Create failed", description: err?.error || err?.message, variant: "destructive" });
    } finally {
      setCreateSaving(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [policiesData, statsData, agentsData] = await Promise.all([
        GatewayPoliciesApi.list(agentFilter || undefined),
        GatewayPoliciesApi.stats(),
        GatewayAgentsApi.list().catch(() => []),
      ]);
      setPolicies(policiesData || []);
      setStatsRaw(statsData || { total: 0, active: 0, permit: 0, forbid: 0 });
      const names = Array.from(
        new Set(((agentsData as any[]) || []).map((a) => a.agentName).filter(Boolean))
      ).sort((a: any, b: any) => String(a).localeCompare(String(b)));
      setAgentOptions(names as string[]);
    } catch (err: any) {
      toast({ title: "Failed to load policies", description: err?.error || err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast, agentFilter]);

  // Re-fetch the tenant list scoped to the chosen agent (empty ⇒ full list); loadData reruns via its dep.
  const applyAgentFilter = (name: string) => {
    setAgentFilter(name);
    setPage(0);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChatSend = async (prompt: string) => {
    const userMsg: ChatMessage = { role: "user", content: prompt };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatLoading(true);
    try {
      const requestData =
        newMessages.length > 1
          ? { messages: newMessages }
          : { prompt };
      const resp = await GatewayPoliciesApi.chat(requestData);

      if (resp?.success === false) {
        toast({ title: "AI error", description: resp.error || "Failed to generate", variant: "destructive" });
        setChatMessages(chatMessages);
        return;
      }

      if (!resp?.conversationComplete && resp?.followUpQuestion) {
        const asstMsg: ChatMessage = { role: "assistant", content: resp.followUpQuestion };
        setChatMessages([...newMessages, asstMsg]);
        return;
      }

      if (resp?.policyText) {
        const asstMsg: ChatMessage = { role: "assistant", content: resp.policyText };
        setChatMessages([...newMessages, asstMsg]);
        setLastChatResp(resp);
        setPolicyCode(resp.policyText);
        return;
      }

      toast({ title: "No policy generated", variant: "destructive" });
      setChatMessages(chatMessages);
    } catch (err: any) {
      toast({ title: "Chat error", description: err?.error || err?.message, variant: "destructive" });
      setChatMessages(chatMessages);
    } finally {
      setChatLoading(false);
    }
  };

  const handleClearChat = () => {
    setChatMessages([]);
    setLastChatResp(null);
    setPolicyCode("");
    setEditingPolicyId(null);
  };

  const handleSavePolicy = async (): Promise<boolean> => {
    const text = policyCode.trim();
    if (!text) {
      toast({ title: "Policy text is empty", variant: "destructive" });
      return false;
    }
    setSaving(true);
    try {
      const body: any = {
        policyName: lastChatResp?.suggestedName || `workspace-policy-${Date.now()}`,
        policyText: text,
        description: lastChatResp?.description || "",
        effect: lastChatResp?.effect || "PERMIT",
        source: lastChatResp ? "LLM_GENERATED" : "MANUAL",
        enabled: true,
      };
      if (editingPolicyId) {
        // preserve priority/tags carried over from the edited policy
        const seed = lastChatResp as any;
        if (seed?.priority != null) body.priority = seed.priority;
        if (seed?.tags) body.tags = seed.tags;
        await GatewayPoliciesApi.update(editingPolicyId, body);
        toast({ title: "Policy updated successfully" });
      } else {
        await GatewayPoliciesApi.create(body);
        toast({ title: "Policy saved successfully" });
      }
      handleClearChat();
      await loadData();
      return true;
    } catch (err: any) {
      toast({ title: "Save failed", description: err?.error || err?.message, variant: "destructive" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!policyCode.trim()) {
      toast({ title: "Nothing to copy" });
      return;
    }
    await copyToClipboard(policyCode);
    toast({ title: "Copied to clipboard" });
  };

  const filteredPolicies = (() => {
    let result = policies.filter((p) => {
      if (filters.effect && p.effect !== filters.effect) return false;
      if (filters.enabled !== "") {
        const enabledBool = filters.enabled === "true";
        if (p.enabled !== enabledBool) return false;
      }
      if (filters.source && p.source !== filters.source) return false;
      return true;
    });
    if (filters.search && filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      result = result.filter((p) =>
        [
          (p as any).policyName,
          p.name,
          p.description,
          (p as any).cedarPolicyId,
          (p as any).principalId,
          Array.isArray((p as any).tags) ? (p as any).tags.join(" ") : (p as any).tags,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return result;
  })()
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") {
        cmp = ((a as any).policyName || a.name || "").localeCompare((b as any).policyName || b.name || "");
      } else {
        cmp =
          new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime();
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

  const totalPages = Math.ceil(filteredPolicies.length / PAGE_SIZE);
  const pagedPolicies = filteredPolicies.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );

  const uniqueSources = Array.from(new Set(policies.map((p) => p.source || "").filter(Boolean)));

  const handleToggleEnabled = async (p: Policy) => {
    const prev = [...policies];
    setPolicies((list) =>
      list.map((pol) => pol.id === p.id ? { ...pol, enabled: !pol.enabled } : pol)
    );
    try {
      await GatewayPoliciesApi.toggle(p.id);
      toast({
        title: p.enabled ? "Policy disabled" : "Policy enabled",
        description: p.policyName || p.name,
      });
    } catch (err: any) {
      setPolicies(prev);
      toast({ title: "Toggle failed", description: err?.error || err?.message, variant: "destructive" });
    }
  };


  const handleDeleteConfirm = async () => {
    if (!deletePolicy) return;
    setDeleteLoading(true);
    try {
      await GatewayPoliciesApi.remove(deletePolicy.id);
      toast({ title: `Policy "${deletePolicy.policyName || deletePolicy.name || deletePolicy.id}" deleted` });
      setDeletePolicy(null);
      await loadData();
    } catch (err: any) {
      toast({ title: "Delete failed", description: err?.error || err?.message, variant: "destructive" });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReloadEngine = async () => {
    try {
      await GatewayPoliciesApi.reload();
      toast({ title: "Policy engine reloaded" });
    } catch (err: any) {
      toast({ title: "Reload failed", description: err?.error || err?.message, variant: "destructive" });
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="w-3.5 h-3.5 inline ml-1" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 inline ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="ag-page">
        <div className="ag-card ag-content">
          <div className="ag-loading">
            <span className="ag-spinner animate-spin" aria-hidden="true" />
            <span>Loading policies…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar ag-nav">
        <div className="cea-tabs ag-tabs" role="tablist" aria-label="Policy views">
          {(["workspace", "library"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={cn("cea-tab", tab === t && "active")}
            >
              {t === "workspace" ? (
                <><Edit /> Workspace</>
              ) : (
                <>
                  <Library /> Policy Library
                  {policies.length > 0 && (
                    <span className="ag-tab-count">{policies.length}</span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
        <div className="ag-toolbar-group">
          {stats?.llmAvailable !== false ? (
            <span className="ag-live" data-tone="success">
              <Brain className="w-3.5 h-3.5" /> AI Active
            </span>
          ) : (
            <span className="ag-live" data-tone="amber">
              No LLM
            </span>
          )}
          <Button
            variant="outline"
            onClick={handleReloadEngine}
            className="ag-button no-default-hover-elevate"
          >
            <RefreshCw /> Reload Engine
          </Button>
          <Button
            onClick={openCreateModal}
            className="ag-button ag-primary-button no-default-hover-elevate"
          >
            <Plus /> Create Policy
          </Button>
        </div>
      </div>

      {tab === "workspace" ? (
        <WorkspaceTab
          seed={seed}
          chatMessages={chatMessages}
          chatLoading={chatLoading}
          lastChatResp={lastChatResp}
          policyCode={policyCode}
          saving={saving}
          onChatSend={handleChatSend}
          onClearChat={handleClearChat}
          onPolicyCodeChange={setPolicyCode}
          onSave={handleSavePolicy}
          onCopy={handleCopyCode}
        />
      ) : (
        <LibraryTab
          policies={pagedPolicies}
          stats={stats}
          filters={filters}
          sortBy={sortBy}
          sortDir={sortDir}
          page={page}
          totalPages={totalPages}
          totalFiltered={filteredPolicies.length}
          uniqueSources={uniqueSources}
          agentFilter={agentFilter}
          agentOptions={agentOptions}
          onApplyAgentFilter={applyAgentFilter}
          onFilterChange={(key, val) => {
            setFilters((f) => ({ ...f, [key]: val }));
            setPage(0);
          }}
          onSortToggle={toggleSort}
          SortIcon={SortIcon}
          onPageChange={setPage}
          onView={setViewPolicy}
          onEdit={openEditModal}
          onDelete={setDeletePolicy}
          onToggle={handleToggleEnabled}
          onReload={handleReloadEngine}
        />
      )}

      <Dialog open={!!viewPolicy} onOpenChange={() => setViewPolicy(null)}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                <Eye />
              </span>
              <div>
                <DialogTitle className="flex items-center gap-2">
                  {viewPolicy && <EffectBadge effect={viewPolicy.effect} />}
                  {(viewPolicy as any)?.policyName || viewPolicy?.name}
                </DialogTitle>
                <DialogDescription>Policy details and Cedar source</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            {viewPolicy && (
              <div className="space-y-4">
                {viewPolicy.description && (
                  <p className="text-sm text-muted-foreground">{viewPolicy.description}</p>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Source</span>
                    <p className="text-foreground/80 mt-0.5">{viewPolicy.source || "-"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status</span>
                    <p className={cn("mt-0.5", viewPolicy.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
                      {viewPolicy.enabled ? "Enabled" : "Disabled"}
                    </p>
                  </div>
                  {(viewPolicy as any).cedarPolicyId && (
                    <div>
                      <span className="text-muted-foreground">Policy ID</span>
                      <p className="text-foreground/80 mt-0.5 font-mono break-all">{(viewPolicy as any).cedarPolicyId}</p>
                    </div>
                  )}
                  {(viewPolicy as any).priority != null && (
                    <div>
                      <span className="text-muted-foreground">Priority</span>
                      <p className="text-foreground/80 mt-0.5">{(viewPolicy as any).priority}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Created</span>
                    <p className="text-foreground/80 mt-0.5">{formatTimestamp(viewPolicy.createdAt)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated</span>
                    <p className="text-foreground/80 mt-0.5">{formatTimestamp(viewPolicy.updatedAt)}</p>
                  </div>
                  {(viewPolicy as any).createdBy && (
                    <div>
                      <span className="text-muted-foreground">Created by</span>
                      <p className="text-foreground/80 mt-0.5">{(viewPolicy as any).createdBy}</p>
                    </div>
                  )}
                </div>
                {(() => {
                  const raw = (viewPolicy as any).tags;
                  const tags: string[] = Array.isArray(raw)
                    ? raw
                    : typeof raw === "string"
                      ? raw.split(",").map((t) => t.trim()).filter(Boolean)
                      : [];
                  return tags.length > 0 ? (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wider">Tags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((t) => (
                          <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary/10 text-primary border border-primary/20">{t}</span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
                {(viewPolicy as any).originalPrompt && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wider">Original Prompt</p>
                    <p className="text-sm text-foreground/70 italic ag-surface rounded-lg px-3 py-2">{(viewPolicy as any).originalPrompt}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Policy Text</p>
                  <GatewayCedarEditor
                    value={viewPolicy.policyText}
                    onChange={() => {}}
                    readOnly
                    className="min-h-[200px]"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              size="sm"
              className="border-border text-muted-foreground hover:text-foreground"
              onClick={async () => {
                if (viewPolicy) {
                  await copyToClipboard(viewPolicy.policyText);
                  toast({ title: "Policy text copied" });
                }
              }}
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Code
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              onClick={() => setViewPolicy(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Create / Edit-metadata Policy modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                {editId ? <Edit /> : <Plus />}
              </span>
              <div>
                <DialogTitle>{editId ? "Edit Policy" : "Create Policy"}</DialogTitle>
                <DialogDescription>Define a Cedar authorization policy, then check it before saving.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Policy Name *</label>
                  <Input value={cf.policyName} onChange={(e) => setCfField("policyName", e.target.value)} placeholder="e.g. block-after-hours" className="ag-surface border-border" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Effect</label>
                  <Select value={cf.effect} onValueChange={(v) => setCfField("effect", v)}>
                    <SelectTrigger className="ag-surface border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="ag-surface border-border">
                      <SelectItem value="PERMIT">PERMIT</SelectItem>
                      <SelectItem value="FORBID">FORBID</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Description</label>
                <Input value={cf.description} onChange={(e) => setCfField("description", e.target.value)} className="ag-surface border-border" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Cedar Policy Text *</label>
                <GatewayCedarEditor value={cf.policyText} onChange={(v: string) => setCfField("policyText", v)} className="h-48" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Priority</label>
                  <Input type="number" value={cf.priority} onChange={(e) => setCfField("priority", e.target.value)} className="ag-surface border-border" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Tags (comma-separated)</label>
                  <Input value={cf.tags} onChange={(e) => setCfField("tags", e.target.value)} placeholder="security, after-hours" className="ag-surface border-border" />
                </div>
              </div>

              {/* Inline check result (mirrors OG #create-validation-result) */}
              {cfChecking ? (
                <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground border-t border-border mt-1 pt-3">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking…
                </div>
              ) : cfCheckError ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-red-500/40 text-red-600 dark:text-red-400 bg-red-500/10 text-sm mt-1">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{cfCheckError}</span>
                </div>
              ) : cfCheckResult ? (
                <div className="border-t border-border mt-1 pt-3">
                  <PolicyCheckResultView c={cfCheckResult} />
                </div>
              ) : null}
            </div>
          </div>
          <DialogFooter className="management-dialog-footer">
            {!cfChecking && cfCheckResult && policyCheckSeverity(cfCheckResult) === "error" && (
              <span className="text-xs text-red-600 dark:text-red-400 mr-auto self-center">Fix the issue above, then re-check.</span>
            )}
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSaving}>Cancel</Button>
            {(() => {
              const sev = cfCheckResult && !cfCheckError ? policyCheckSeverity(cfCheckResult) : null;
              const saveable = !cfChecking && sev !== null && sev !== "error";
              if (saveable) {
                return (
                  <>
                    <Button
                      variant="outline"
                      onClick={runCreateCheck}
                      disabled={createSaving}
                    >
                      <FlaskConical className="w-3.5 h-3.5 mr-1.5" /> Re-check
                    </Button>
                    <Button
                      onClick={handleCreateManual}
                      disabled={createSaving}
                      className={sev === "warn" ? "ag-warning-button" : "endpoint-primary-action"}
                    >
                      {createSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                      {sev === "warn" ? "Save anyway" : editId ? "Save" : "Save"}
                    </Button>
                  </>
                );
              }
              return (
                <Button
                  onClick={runCreateCheck}
                  disabled={cfChecking || createSaving}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)]"
                >
                  {cfChecking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5 mr-1.5" />}
                  Check policy
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletePolicy} onOpenChange={() => setDeletePolicy(null)}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Trash2 />
              </span>
              <div>
                <DialogTitle>Delete Policy</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete{" "}
                  <strong className="font-mono">{deletePolicy?.policyName || deletePolicy?.name || deletePolicy?.id}</strong>? This action
                  cannot be undone.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              onClick={() => setDeletePolicy(null)}
            >
              Cancel
            </Button>
            <Button
              className="ag-danger-button"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              {deleteLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


interface WorkspaceTabProps {
  seed?: string;
  chatMessages: ChatMessage[];
  chatLoading: boolean;
  lastChatResp: PolicyChatResponse | null;
  policyCode: string;
  saving: boolean;
  onChatSend: (msg: string) => void;
  onClearChat: () => void;
  onPolicyCodeChange: (val: string) => void;
  onSave: () => Promise<boolean>;
  onCopy: () => void;
}

function WorkspaceTab({
  seed,
  chatMessages,
  chatLoading,
  lastChatResp,
  policyCode,
  saving,
  onChatSend,
  onClearChat,
  onPolicyCodeChange,
  onSave,
  onCopy,
}: WorkspaceTabProps) {
  const { toast } = useToast();
  // Single "Check policy" gate → review popup → Save / Save anyway (mirrors OG proceed()).
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const runCheck = async () => {
    // ignore whitespace / comment-only drafts
    const meaningful = policyCode.replace(/\/\/[^\n]*/g, "").trim();
    if (!policyCode.trim() || !meaningful) {
      toast({ title: "Write a policy first", variant: "destructive" });
      return;
    }
    setReviewOpen(true);
    setChecking(true);
    setCheckResult(null);
    setCheckError(null);
    try {
      setCheckResult(await GatewayPoliciesApi.check(policyCode));
    } catch (e: any) {
      setCheckError(e?.error || e?.message || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  const handleSaveFromReview = async () => {
    const ok = await onSave();
    if (ok) setReviewOpen(false);
  };

  return (
    <div className="grid grid-cols-2 gap-6 flex-1 min-h-[560px]">
      <div className="ag-card overflow-hidden flex flex-col min-h-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-medium text-foreground/80">Policy Assistant</span>
          {chatMessages.length > 0 && (
            <button
              onClick={onClearChat}
              className="text-xs text-muted-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> New
            </button>
          )}
        </div>
        <GatewayChatPanel
          initialInput={seed}
          messages={chatMessages}
          onSend={onChatSend}
          loading={chatLoading}
          welcomeMessage="I can help you create Cedar authorization policies."
          suggestions={[
            "Block all tool calls after hours",
            "Allow read-only access to database tools",
            "Require human approval for destructive actions",
          ]}
          placeholder={
            chatMessages.length > 0
              ? "Refine this policy or ask a follow-up..."
              : "Describe your policy in natural language..."
          }
          className="flex-1 min-h-0"
        />
      </div>

      <div className="flex flex-col gap-6 min-h-0">
        <div className="ag-card overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <span className="text-sm font-medium text-foreground/80">Cedar Policy Editor</span>
            <div className="flex items-center gap-2">
              <button
                onClick={onCopy}
                className="p-1.5 rounded-md hover:bg-primary/[0.025] text-muted-foreground hover:text-foreground/80 transition-colors"
                title="Copy code"
              >
                <Copy className="w-4 h-4" />
              </button>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)] text-xs"
                onClick={runCheck}
                disabled={checking || saving || !policyCode.trim()}
                title="Check, then save"
              >
                {checking ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
                )}
                Check policy
              </Button>
            </div>
          </div>
          {lastChatResp?.semanticWarning && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs">
              <ShieldX className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1">{lastChatResp.semanticWarning}</span>
            </div>
          )}
          <div className="flex-1 min-h-0 p-3">
            <GatewayCedarEditor
              value={policyCode}
              onChange={onPolicyCodeChange}
              className="h-full"
            />
          </div>
        </div>

        <PolicyContextPanel resp={lastChatResp} />
      </div>

      <PolicyReviewDialog
        open={reviewOpen}
        onOpenChange={(v) => !saving && setReviewOpen(v)}
        loading={checking}
        result={checkResult}
        error={checkError}
        saving={saving}
        onSave={handleSaveFromReview}
      />
    </div>
  );
}

function PolicyContextPanel({ resp }: { resp: PolicyChatResponse | null }) {
  return (
    <div className="ag-card p-4 space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Policy Context</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</p>
          <p className="text-sm text-foreground/80 mt-0.5">{resp?.suggestedName || "-"}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Effect</p>
          <div className="mt-0.5">
            {resp?.effect ? <EffectBadge effect={resp.effect} /> : <span className="text-sm text-muted-foreground">-</span>}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Source</p>
          <p className="text-sm text-foreground/80 mt-0.5">
            {resp?.source === "LLM_GENERATED" ? (
              <span className="flex items-center gap-1 text-primary">
                <Brain className="w-3.5 h-3.5" /> AI Generated
              </span>
            ) : resp?.source ? resp.source : "-"}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</p>
          <p className="text-sm text-foreground/80 mt-0.5 truncate" title={resp?.description}>
            {resp?.description || "-"}
          </p>
        </div>
      </div>
      {resp?.explanation && (
        <p className="text-xs text-muted-foreground italic border-t border-border pt-2 leading-relaxed">
          {resp.explanation}
        </p>
      )}
    </div>
  );
}


interface LibraryTabProps {
  policies: Policy[];
  stats: PolicyStats;
  filters: LibraryFilters;
  sortBy: SortField;
  sortDir: SortDir;
  page: number;
  totalPages: number;
  totalFiltered: number;
  uniqueSources: string[];
  agentFilter: string;
  agentOptions: string[];
  onApplyAgentFilter: (name: string) => void;
  onFilterChange: (key: string, val: string) => void;
  onSortToggle: (field: SortField) => void;
  SortIcon: React.ComponentType<{ field: SortField }>;
  onPageChange: (p: number) => void;
  onView: (p: Policy) => void;
  onEdit: (p: Policy) => void;
  onDelete: (p: Policy) => void;
  onToggle: (p: Policy) => void;
  onReload: () => void;
}

// The principal a policy scopes to — from the gateway's derived read-model (principalKind/principalId).
function ScopeBadge({ policy }: { policy: Policy }) {
  const kind = (policy as any).principalKind || "ANY";
  const id = (policy as any).principalId || "";
  if (kind === "AGENT")
    return (
      <span
        title={`Targets this agent directly (principal == Agent::"${id}")`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20"
      >
        <Bot className="w-3 h-3 shrink-0" /> {id}
      </span>
    );
  if (kind === "AGENT_GROUP")
    return (
      <span
        title="Applies to members of an agent group"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ag-surface text-muted-foreground border border-border"
      >
        <Users className="w-3 h-3 shrink-0" /> Group: {id}
      </span>
    );
  if (kind === "AGENT_TYPE")
    return (
      <span
        title="Applies to all agents of this type"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ag-surface text-muted-foreground border border-border"
      >
        <Shapes className="w-3 h-3 shrink-0" /> Type: {id}
      </span>
    );
  return (
    <span
      title="Applies to every agent (wildcard / baseline guardrail)"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
    >
      <Globe className="w-3 h-3 shrink-0" /> All agents
    </span>
  );
}

function LibraryTab({
  policies,
  stats,
  filters,
  sortBy,
  sortDir,
  page,
  totalPages,
  totalFiltered,
  uniqueSources,
  agentFilter,
  agentOptions,
  onApplyAgentFilter,
  onFilterChange,
  onSortToggle,
  SortIcon,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onToggle,
  onReload,
}: LibraryTabProps) {
  return (
    <>
      <div className="ag-stat-grid">
        <GatewayStatCard
          icon={<Library className="w-5 h-5" />}
          label="Total"
          value={stats.total ?? 0}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Active"
          value={stats.active ?? 0}
          colorClass="text-emerald-600 dark:text-emerald-400"
        />
        <GatewayStatCard
          icon={<ShieldCheck className="w-5 h-5" />}
          label="PERMIT"
          value={stats.permit ?? 0}
          colorClass="text-emerald-600 dark:text-emerald-400"
        />
        <GatewayStatCard
          icon={<ShieldX className="w-5 h-5" />}
          label="FORBID"
          value={stats.forbid ?? 0}
          colorClass="text-red-600 dark:text-red-400"
        />
      </div>

      <div className="ag-card ag-content">
        <div className="ag-card-header ag-card-header-bordered ag-filter-row">
          <div className="ag-search ag-search-grow">
            <Search aria-hidden="true" />
            <input
              type="text"
              placeholder="Search by policy name, ID, agent, or keywords..."
              value={filters.search}
              onChange={(e) => onFilterChange("search", e.target.value)}
            />
          </div>
          <Select value={filters.effect || "__all__"} onValueChange={(v) => onFilterChange("effect", v === "__all__" ? "" : v)}>
            <SelectTrigger className="ag-select w-[160px]">
              <SelectValue placeholder="All effects" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="__all__">All effects</SelectItem>
              <SelectItem value="PERMIT">PERMIT</SelectItem>
              <SelectItem value="FORBID">FORBID</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.enabled || "__all__"} onValueChange={(v) => onFilterChange("enabled", v === "__all__" ? "" : v)}>
            <SelectTrigger className="ag-select w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="true">Enabled</SelectItem>
              <SelectItem value="false">Disabled</SelectItem>
            </SelectContent>
          </Select>
          {uniqueSources.length > 0 && (
            <Select value={filters.source || "__all__"} onValueChange={(v) => onFilterChange("source", v === "__all__" ? "" : v)}>
              <SelectTrigger className="ag-select w-[180px]">
                <SelectValue placeholder="All sources" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="__all__">All sources</SelectItem>
                {uniqueSources.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {agentOptions.length > 0 && (
            <Select value={agentFilter || "__all__"} onValueChange={(v) => onApplyAgentFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="ag-select w-[190px]">
                <SelectValue placeholder="Agent: All" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="__all__">Agent: All</SelectItem>
                {agentOptions.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="ag-scroll">
          {policies.length === 0 ? (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon" aria-hidden="true">
                <Library />
              </span>
              <p>No policies match your filters</p>
            </div>
          ) : (
            <div className="ag-table-wrap">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"14%"}}>
                      <button onClick={() => onSortToggle("name")} className="flex items-center gap-1 hover:text-foreground transition-colors">
                        Policy Name <SortIcon field="name" />
                      </button>
                    </th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"18%"}}>Description</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"7%"}}>Effect</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"7%"}}>Source</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"12%"}}>Scope</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"11%"}}>Tags</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"11%"}}>
                      <button onClick={() => onSortToggle("createdAt")} className="flex items-center gap-1 hover:text-foreground transition-colors">
                        Created <SortIcon field="createdAt" />
                      </button>
                    </th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"10%"}}>Status</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"10%"}}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {policies.map((policy) => {
                    const tags: string[] = (() => {
                      const raw = policy.tags;
                      if (!raw) return [];
                      if (Array.isArray(raw)) return raw;
                      if (typeof raw === "string") return raw.split(",").map((t: string) => t.trim()).filter(Boolean);
                      return [];
                    })();
                    // system guardrails (source=DEFAULT) are read-only: backend refuses toggle/edit/delete
                    const isProtected = policy.source === "DEFAULT";
                    return (
                    <tr
                      key={policy.id}
                      className={cn(
                        "hover:bg-primary/[0.025] transition-colors",
                        !policy.enabled && "opacity-60"
                      )}
                    >
                      <td className="px-4 py-3">
                        <span className={cn(
                          "font-semibold text-sm leading-6",
                          policy.enabled ? "text-foreground" : "text-foreground/80"
                        )}>
                          {policy.policyName || policy.name}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={cn(
                          "text-xs line-clamp-2 break-words leading-6",
                          policy.enabled ? "text-foreground/80" : "text-muted-foreground"
                        )} title={policy.description || ""}>
                          {policy.description || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <EffectBadge effect={policy.effect} />
                      </td>
                      <td className="px-3 py-3">
                        {policy.source ? (
                          <span className={cn(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border",
                            policy.source === "LLM_GENERATED"
                              ? "bg-primary/10 text-primary border-primary/20"
                              : isProtected
                                ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                                : "ag-surface text-muted-foreground border-border"
                          )}>
                            {policy.source === "LLM_GENERATED" && <Brain className="w-3 h-3" />}
                            {isProtected && <Lock className="w-3 h-3" />}
                            {policy.source === "LLM_GENERATED" ? "AI" : isProtected ? "System" : policy.source}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/40">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <ScopeBadge policy={policy} />
                      </td>
                      <td className="px-3 py-3">
                        {tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary/10 text-primary border border-primary/20"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground/40">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-muted-foreground">{formatTimestamp(policy.createdAt)}</span>
                      </td>
                      <td className="px-3 py-3">
                        {isProtected ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Lock className="w-3 h-3" /> Always on
                          </span>
                        ) : (
                        <button
                          onClick={() => onToggle(policy)}
                          className="flex items-center gap-1.5 transition-colors"
                          title={policy.enabled ? "Click to disable" : "Click to enable"}
                        >
                          <div
                            className={cn(
                              "relative w-9 h-[18px] rounded-full transition-colors duration-200",
                              policy.enabled ? "bg-emerald-500" : "bg-muted-foreground/30"
                            )}
                          >
                            <div
                              className={cn(
                                "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform duration-200",
                                policy.enabled ? "translate-x-[19px]" : "translate-x-[2px]"
                              )}
                            />
                          </div>
                          <span className={cn(
                            "text-xs",
                            policy.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                          )}>
                            {policy.enabled ? "Active" : "Disabled"}
                          </span>
                        </button>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => onView(policy)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!isProtected && (
                          <button
                            onClick={() => onEdit(policy)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-500/10 text-muted-foreground hover:text-amber-400 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          )}
                          {!isProtected && (
                          <button
                            onClick={() => onDelete(policy)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>

        {totalFiltered > 0 && (
          <div className="ag-pagination">
            <span className="ag-pagination-summary">
              Showing {Math.min(totalFiltered, (page + 1) * 10)} of {totalFiltered}
            </span>
            <CardPagination
              label="Policy pages"
              currentPage={page + 1}
              totalPages={Math.max(1, totalPages)}
              onPageChange={(p) => onPageChange(p - 1)}
            />
          </div>
        )}
      </div>
    </>
  );
}
